import React from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';

import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import ImageLazy from '@common/react/components/UI/ImageLazy/ImageLazy';

import LastTab from '@app/components/Pages/Books/LastTab';
import Background from '@app/components/UI/Background/Background';
import Card from '@app/components/UI/Card/Card';
import Footprint from '@app/components/UI/Footprint/Footprint';
import { HelmetTags } from '@app/components/UI/Helmet/HelmetCustom';
import Tabs from '@app/components/UI/Tabs/Tabs';
import BackgroundSVG from '@app/imgs/BackgroundSVG';
import Star from '@app/imgs/Star';
import { Book, BookCategory, BookReleases } from '@app/objects/BookCategory';

import '@app/scss/pages/Books.scss';

const drawStarsYellow = (arr, num) => {
	let i = 0;

	while (i < num) {
		i++;
		arr.push(<Star color="#FFDD60" />);
	}
};

const drawStarsGray = (arr, num) => {
	let i = 0;

	while (i < num) {
		i++;
		arr.push(<Star color="#8E93A0" />);
	}
};

const drawStars = (yellow, gray) => {
	const stars: Array<React.ReactNode> = [];

	drawStarsYellow(stars, yellow);
	drawStarsGray(stars, gray);

	return stars.map((star, index) => <React.Fragment key={index}>{star}</React.Fragment>);
};

export const checkRating = (rating: number) => {
	const maxStars = 5;
	const num = Math.round(rating);

	return drawStars(num, maxStars - num);
};

interface BookCardsProps {
	books: Array<Book>;
	location?: any;
}

const BookCards: React.FC<BookCardsProps> = ({ books, location }) => {
	return (
		<>
			{books.length ? (
				books.map((book) => (
					<Card
						key={book.id}
						id={book.urlPath}
						className="card_image-shadow card-border scroll-top"
						image={
							<>
								<BackgroundSVG />
								<ImageLazy className="image-dog" alt={`Photo book - ${book.name}`} src={book.avatar} />
							</>
						}
						text={
							<div className="book-desc">
								<h2>{book.name}</h2>
								<div>
									{book.releases.map((elem: BookReleases, index) => {
										if (elem.dateTime) {
											const date = new Date(elem.dateTime);
											const formattedDate = date.toLocaleDateString('en-US', {
												timeZone: 'UTC',
												month: 'long',
												year: 'numeric',
												day: 'numeric',
											});
											return (
												<p key={index}>
													{`
														${elem.name} - ${formattedDate}
													`}
												</p>
											);
										}
										return <p key={index}>{elem.customDescription}</p>;
									})}
								</div>
								<div className="rating-block">
									<div>
										<strong>{book.rating}</strong>
									</div>
									<div className="block-stars">{checkRating(book.rating)}</div>
									<div>
										<a href="#">
											{book.ratingsCount}
											{' '}
											ratings
										</a>
									</div>
								</div>
								<div className="description" dangerouslySetInnerHTML={{ __html: book.shortDescription }} />
								<NavLink to={`${location.pathname}/${book.urlPath}`}>
									<button className="btn btn-primary">
										<Footprint />
										About book
									</button>
								</NavLink>
							</div>
						}
					/>
				))
			) : (
				<div className="h3 text-center unavailable">Books in this section are temporarily unavailable.</div>
			)}
		</>
	);
};

const Books: React.FC = () => {
	const [defaultPath, setDefaultPath] = React.useState<string>();
	const location = useLocation();
	const history = useHistory();

	const changePath = (key) => {
		history.push(key);
	};

	React.useEffect(() => {
		if (!location.pathname.includes('books/')) {
			setDefaultPath('dogs');
			history.push('books/dogs');
		} else {
			setDefaultPath(location.pathname.slice(location.pathname.lastIndexOf('/') + 1));
		}

		const hashName = location.hash;
		if (hashName) {
			window.location.hash = '';
			setTimeout(() => {
				window.location.hash = hashName;
			}, 500);
		}
	}, [location.pathname]);

	return (
		<ServerPageProvider
			path="/books"
			inner={(page) => {
				return (
					<div className="is-relative pages">
						<HelmetTags lang={page.langs[0]} title="Books" />
						<Background />
						<div className="books-page">
							<div className="container">
								<h1 className="h2 text-center">Books</h1>
								<ServerPageProvider
									path="books"
									ttl={600000}
									inner={(page) => {
										const items = page.items.list.map((item: BookCategory) => {
											return {
												key: item.urlPath,
												label: item.name,
												children: <BookCards books={item.books} location={location} />,
											};
										});

										items.push({
											key: 'recommended-books',
											label: 'Recommended books',
											children: <LastTab />,
										});

										return <Tabs items={items} defaultKey={defaultPath} onTabChange={changePath} />;
									}}
								/>
							</div>
						</div>
					</div>
				);
			}}
		/>
	);
};

export default Books;
